// src/ProfileLayout.jsx
import React, { useEffect, useState } from 'react';
import { NavLink, Routes, Route, Outlet } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import Profile from './Profile';
import ProfileTickets from './ProfileTickets';
import ProfileSkins from './ProfileSkins';
import ProfileLevel from './ProfileLevel';
import './tabs.css';
import ProfileDeposit from './ProfileDeposit';
import TopUpFiatModal from './components/TopUpFiatModal';
import ProfileAffiliate from './ProfileAffiliate';
import ProfileClub from './ProfileClub';
import ProfileSecurity from './ProfileSecurity';
import ProfileWithdraw from './ProfileWithdraw';
import './ProfileLayoutMobile.css';

export default function ProfileLayout({
  username,
  userAssets,
  userBalance,
  setUsername,
}) {
  const { t: translate } = useTranslation();
  const [isPartner, setIsPartner] = useState(false);
  const [, setPartnerLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');

        if (!token) {
          setIsPartner(false);
          setPartnerLoading(false);
          return;
        }

        const API_ORIGIN = process.env.REACT_APP_API_URL;
        const API_BASE = `${API_ORIGIN}/api`;

        const resp = await axios.get(`${API_BASE}/auth/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log(
          '[ProfileLayout] /api/profile resp.data =',
          resp.data
        );

        const me = resp.data || {};
        const partnerFlag = Boolean(
          me.is_partner ?? me.isPartner
        );

        setIsPartner(partnerFlag);
      } catch (error) {
        console.error(
          '[ProfileLayout] ошибка загрузки профиля для партнёрки',
          error
        );

        setIsPartner(false);
      } finally {
        setPartnerLoading(false);
      }
    };

    fetchProfile();
  }, []);

  /*
    На игровых страницах сайт остаётся без постоянного mobile viewport,
    потому что игровые доски рассчитаны на старую широкую вёрстку.

    Но пока открыт любой раздел /profile, включаем нормальную мобильную
    ширину. Поэтому одинаково крупно отображаются:
    Аккаунт, Билеты, Скины, Уровень, Безопасность, Депозит, Вывод,
    Партнёрка и Клуб.
  */
  useEffect(() => {
    let viewportMeta = document.querySelector(
      'meta[name="viewport"]'
    );

    const existedBefore = Boolean(viewportMeta);
    const previousContent =
      viewportMeta?.getAttribute('content') || null;

    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.head.appendChild(viewportMeta);
    }

    viewportMeta.setAttribute(
      'content',
      'width=device-width, initial-scale=1, viewport-fit=cover'
    );

    /*
      club-mobile-page оставлен для совместимости с уже готовым
      ProfileClub.css. Теперь этот класс действует во всём профиле.
    */
    document.body.classList.add(
      'profile-mobile-page',
      'club-mobile-page'
    );

    const refreshLayout = () => {
      window.dispatchEvent(new Event('resize'));
    };

    refreshLayout();
    const timer = window.setTimeout(refreshLayout, 50);

    return () => {
      window.clearTimeout(timer);

      document.body.classList.remove(
        'profile-mobile-page',
        'club-mobile-page'
      );

      if (existedBefore && previousContent) {
        viewportMeta.setAttribute(
          'content',
          previousContent
        );
      } else {
        /*
          Возвращаем старое поведение сайта после выхода из профиля,
          чтобы игровые доски снова отображались как раньше.
        */
        viewportMeta.setAttribute(
          'content',
          'width=980'
        );
      }

      refreshLayout();
    };
  }, []);

  return (
    <div
      className="profile-layout"
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 20px',
        color: '#fff',
        background: '#111827',
        overflow: 'hidden',
      }}
    >
      <h2 style={{ margin: '0 0 16px' }}>
        {translate('profile.title')}
      </h2>

      <div className="profile-tabs">
        <NavLink
          end
          to="."
          className={({ isActive }) =>
            'profile-tab' +
            (isActive ? ' is-active' : '')
          }
        >
          {translate('profile.tabs.account')}
        </NavLink>

        <NavLink
          to="tickets"
          className={({ isActive }) =>
            'profile-tab' +
            (isActive ? ' is-active' : '')
          }
        >
          {translate('profile.tabs.tickets')}
        </NavLink>

        <NavLink
          to="skins"
          className={({ isActive }) =>
            'profile-tab' +
            (isActive ? ' is-active' : '')
          }
        >
          {translate('profile.tabs.skins')}
        </NavLink>

        <NavLink
          to="level"
          className={({ isActive }) =>
            'profile-tab' +
            (isActive ? ' is-active' : '')
          }
        >
          {translate('profile.tabs.level')}
        </NavLink>

        <NavLink
          to="security"
          className={({ isActive }) =>
            'profile-tab' +
            (isActive ? ' is-active' : '')
          }
        >
          {translate('profile.tabs.security')}
        </NavLink>

        <NavLink
          to="deposit"
          className={({ isActive }) =>
            'profile-tab' +
            (isActive ? ' is-active' : '')
          }
        >
          {translate('profile.tabs.deposit')}
        </NavLink>

        <NavLink
          to="withdraw"
          className={({ isActive }) =>
            'profile-tab' +
            (isActive ? ' is-active' : '')
          }
        >
          {translate('profile.tabs.withdraw')}
        </NavLink>

        {isPartner && (
          <NavLink
            to="affiliate"
            className={({ isActive }) =>
              'profile-tab' +
              (isActive ? ' is-active' : '')
            }
          >
            {translate('profile.tabs.affiliate')}
          </NavLink>
        )}

        <NavLink
          to="club"
          className={({ isActive }) =>
            'profile-tab' +
            (isActive ? ' is-active' : '')
          }
        >
          {translate('profile.tabs.club')}
        </NavLink>
      </div>

      <div
        className="profile-tabpanel"
        style={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        <Routes>
          <Route
            index
            element={
              <Profile
                setUsername={setUsername}
              />
            }
          />

          <Route
            path="tickets"
            element={
              <ProfileTickets
                tickets={
                  userAssets?.tickets || []
                }
              />
            }
          />

          <Route
            path="skins"
            element={
              <ProfileSkins
                skins={
                  userAssets?.skins || []
                }
              />
            }
          />

          <Route
            path="level"
            element={
              <ProfileLevel
                username={username}
              />
            }
          />

          <Route
            path="security"
            element={<ProfileSecurity />}
          />

          <Route
            path="affiliate"
            element={
              isPartner ? (
                <ProfileAffiliate
                  username={username}
                />
              ) : (
                <div
                  style={{
                    padding: 20,
                    color: '#f87171',
                    fontSize: 14,
                  }}
                >
                  {translate('profile.partnerOnly')}
                </div>
              )
            }
          />

          <Route
            path="club"
            element={
              <ProfileClub
                isPartner={isPartner}
                userBalance={userBalance}
              />
            }
          />

          <Route
            path="deposit"
            element={
              <div style={{ padding: 20 }}>
                <h2
                  style={{
                    marginBottom: 16,
                  }}
                >
                  {translate('profile.deposit.title')}
                </h2>

                <div
                  style={{
                    display: 'flex',
                    gap: 24,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                  }}
                >
                  <div
                    style={{
                      flex: '1 1 360px',
                      maxWidth: 420,
                    }}
                  >
                    <ProfileDeposit
                      username={username}
                    />
                  </div>

                  <div
                    style={{
                      flex: '1 1 360px',
                      maxWidth: 420,
                    }}
                  >
                    <TopUpFiatModal />
                  </div>
                </div>
              </div>
            }
          />

          <Route
            path="withdraw"
            element={<ProfileWithdraw />}
          />
        </Routes>

        <Outlet />
      </div>
    </div>
  );
}
